import React, { useState, useRef, useMemo, useEffect } from 'react';
import { TextField, Paper, Popper, Box, IconButton, InputAdornment, ClickAwayListener } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

export default function TreeSelect({ 
  data = [], 
  value = '', 
  onChange, 
  placeholder = "Chọn...", 
  width = 450 
}) {
  const [searchText, setSearchText] = useState('');
  const [openCombo, setOpenCombo] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  
  const inputRef = useRef(null);

  // --- HÀM TRỢ GIÚP ĐỆ QUY (RECURSIVE HELPERS) ---

  // 1. Thu thập tất cả ID của những node có chứa thư mục con (dùng để chặn chọn node cha và dùng để auto expand)
  const folderIds = useMemo(() => {
    const ids = [];
    const collectFolderIds = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          ids.push(node.id);
          collectFolderIds(node.children); // Đệ quy vào sâu hơn
        }
      });
    };
    collectFolderIds(data);
    return ids;
  }, [data]);

  // 2. Tìm kiếm nhãn tên hiển thị (label) của một ID bất kỳ trong cây đa cấp
  const findLabelById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node.label;
      if (node.children && node.children.length > 0) {
        const foundLabel = findLabelById(node.children, id);
        if (foundLabel) return foundLabel;
      }
    }
    return null;
  };

  // 3. Đệ quy lọc dữ liệu theo từ khóa tìm kiếm
  const filterTreeData = (nodes, query) => {
    return nodes.map(node => {
      const isMatch = node.label.toLowerCase().includes(query);
      
      // Nếu có node con, tiến hành lọc các node con trước
      if (node.children && node.children.length > 0) {
        const filteredChildren = filterTreeData(node.children, query);
        
        // Giữ lại node này nếu chính nó khớp từ khóa HOẶC có ít nhất 1 node con khớp từ khóa
        if (isMatch || filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren
          };
        }
      } else if (isMatch) {
        // Nếu là node lá (không có con) và khớp từ khóa thì giữ lại
        return node;
      }
      return null;
    }).filter(Boolean);
  };

  // 4. HÀM ĐỆ QUY QUAN TRỌNG NHẤT: Vẽ giao diện TreeItem dựa theo số cấp dữ liệu
  const renderTreeItems = (nodes) => {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      return (
        <TreeItem key={node.id} itemId={node.id} label={node.label}>
          {hasChildren ? renderTreeItems(node.children) : null}
        </TreeItem>
      );
    });
  };

  // --- CÁC EFFECT XỬ LÝ SỰ KIỆN TRẠNG THÁI ---

  // Cập nhật ô nhập liệu khi giá trị `value` từ màn hình cha thay đổi
  useEffect(() => {
    if (value) {
      const label = findLabelById(data, value);
      setSearchText(label || value);
    } else {
      setSearchText('');
    }
  }, [value, data]);

  // Tự động bung toàn bộ các nhánh cây khi đang gõ từ khóa tìm kiếm
  useEffect(() => {
    if (searchText.trim()) {
      setExpandedItems(folderIds);
    } else {
      setExpandedItems([]);
    }
  }, [searchText, folderIds]);

  // Tính toán dữ liệu sau khi lọc tìm kiếm
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    
    // Nếu nội dung ô input trùng khớp hoàn toàn với item đang chọn thì hiển thị toàn bộ cây gốc
    const currentSelectedLabel = findLabelById(data, value);
    if (currentSelectedLabel === searchText) return data;

    return filterTreeData(data, searchText.toLowerCase());
  }, [searchText, data, value]);

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchText('');
    if (onChange) onChange('');
    setOpenCombo(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpenCombo(false)}>
      <Box sx={{ width: width, position: 'relative' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          autoComplete='off'
          placeholder={placeholder}
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setOpenCombo(true);
          }}
          onFocus={() => setOpenCombo(true)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchText && (
                  <ClearIcon fontSize="small" onClick={handleClear} sx={{cursor: 'pointer'}} />
                )}
                <IconButton onClick={() => setOpenCombo(!openCombo)} edge="end">
                  {openCombo ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Popper
          open={openCombo}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ 
            width: inputRef.current ? inputRef.current.clientWidth : 'auto',
            zIndex: 1300
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              mt: 1, 
              p: 1, 
              backgroundColor: '#2d3748', 
              color: '#ffffff',
              maxHeight: 350,
              overflowY: 'auto'
            }}
          >
            <SimpleTreeView
              expandedItems={expandedItems}
              onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
              onSelectedItemsChange={(event, itemId) => {
                // Chặn không cho chọn các node cha (những node có chứa thư mục con)
                if (itemId && !folderIds.includes(itemId)) {
                  if (onChange) onChange(itemId);
                  setOpenCombo(false); 
                }
              }}
              sx={{
                '& .MuiTreeItem-label': { color: '#e2e8f0', py: 0.5 },
                '& .MuiTreeItem-iconContainer': { color: '#a0aec0' },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.12) !important' }
              }}
            >
              {/* Gọi hàm đệ quy để tự động render mọi cấp dữ liệu */}
              {renderTreeItems(filteredData)}
            </SimpleTreeView>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}